import React from 'react';
import { images } from '../../components/shared/assets';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { usePageContent, useArtImmersions, useSiteSettings } from '../../hooks/useContentful';
import { ContentfulText, ContentfulMultilineText } from '../../components/Contentful';
import {
  ArtDiptych,
  ArticleGallerySubheader,
  ArticleGallery,
  FeathersVideo,
  FeathersVideoFooter
} from '../../pages/ArtImmersions/components';
import '../ArtImmersions/ArtImmersionsPage.css';

const ArtImmersionsAtacama: React.FC = () => {
  // Fetch content from Contentful
  const { data: pageContentData } = usePageContent('art-immersions');
  const { data: artImmersionsData } = useArtImmersions();
  const { data: siteSettingsData } = useSiteSettings();
  const pageContent = pageContentData?.[0]?.fields as any;
  const siteSettings = siteSettingsData?.[0]?.fields as any;
  
  // Use Contentful art immersions if available, otherwise fall back to static data
  const contentfulImmersions = artImmersionsData?.map((immersion: any) => ({
    id: immersion.sys.id,
    title: immersion.fields.title,
    location: immersion.fields.location,
    date: immersion.fields.date ? new Date(String(immersion.fields.date)).toLocaleDateString() : '',
    description: immersion.fields.description,
    image: immersion.fields.featuredImage?.fields?.file?.url ? `https:${immersion.fields.featuredImage.fields.file.url}` : '',
    hasVideo: immersion.fields.hasVideo || false
  })) || [];
  
  // Static art immersions data as fallback
  const staticArtImmersions = [
    {
      id: 'ingrid-weyland-topographies',
      title: 'Ingrid Weyland - Topographies of Fragility',
      location: 'AWASI IGUAZÚ - 03.02.2022',
      date: '03.02.2022',
      description: 'The Argentine-born artist and photographer employs a unique technique in her work: she applies crumpled images on top of her photographs to highlight the impact man has on nature.',
      image: "https://images.ctfassets.net/hz1mviiqoqof/7m206lCUA2JGr4gD2GXwOA/76f0c0f0278e2910f8a37ad95374c6b9/art-ingrid-weyland.png",
      hasVideo: false
    },
    {
      id: 'teresa-marcaida-nature-art',
      title: 'Teresa Marcaida  - "Peculiar Quietness"',
      location: 'AWASI IGUAZÚ - 03.02.2022',
      date: '03.02.2022',
      description: 'Teresa Marcaida is a dancer and choreographer who studied at the Juilliard School in New York. She visited Awasi Atacama and Awasi Iguazú to film this performance as an homage to "nature as a form of art".',
      image: "https://images.ctfassets.net/hz1mviiqoqof/YUYkDXP8n3MhkyfLfvRgL/55ad9dcbb368532f7fd5b2260ff524c6/art-teresa-marcaida.png",
      hasVideo: true
    },
    {
      id: 'rainer-gross-double-takes',
      title: 'Rainer Gross - "Double Takes"',
      location: 'AWASI - 03.02.2022',
      date: '03.02.2022',
      description: 'Rainer Gross, a renowned German artist based between New York City and Long Island, visited Awasi Iguazu where he was captivated by the raw power of nature. Combining his passions for painting and photography, he created stunning images of the waterfalls, inspired by the vibrant colors of the Atlantic Rainforest.',
      image: "https://images.ctfassets.net/hz1mviiqoqof/2LGBwJ5lhRPqrO2hnlKQ41/80ca464571d58eae781a674d4732e35c/art-rainer-gross.png",
      hasVideo: true
    },
    {
      id: 'iliana-diaz-portales',
      title: 'Iliana Díaz López - "Portales del Ara Pyau"',
      location: 'AWASI - 03.02.2022',
      date: '03.02.2022',
      description: 'Argentine and London-based sound engineer, singer and founder of The Factory Sound, Iliana Díaz López, visited Awasi Iguazu to celebrate the Ara Pyau, the Guaraní New Year. The artist welcomed the Ara Pyau with a live performance, accompanied by the Guaraní Yriapu community choir.',
      image: "https://images.ctfassets.net/hz1mviiqoqof/3YJfvRUFSEQMhu09O7TTNn/f9e168bf105d99df142d449221e6ceea/art-iliana-diaz.png",
      hasVideo: true
    },
    {
      id: 'andres-paredes-trama',
      title: 'Andrés Paredes - "Trama Y Desenlace"',
      location: 'AWASI - 02.11.2023',
      date: '02.11.2023',
      description: 'Argentine visual artist Andrés Paredes recently spent time with the team at Awasi Iguazú. Whilst in-residence, Andrés installed a biodegradable sculpture deep in the Atlantic rainforest near to the lodge.',
      image: images.artIngridWeyland || '',
      hasVideo: false
    },
    {
      id: 'cecilia-nigro-cuando',
      title: 'Cecilia Nigro - "Cuando Me Baje El Alma Al Cuerpo"',
      location: 'AWASI - 02.01.2024',
      date: '02.01.2024',
      description: 'Cecilia Nigro is an argentine ceramist. She visited Awasi Iguazu to experiment with Ñaú mud, a material found along riverbanks and used by Guaraní communities to make pottery. The artist met with a local artisan and learned traditional weaving techniques using canes from the Atlantic Rainforest.',
      image: images.artTeresaMarcaida || '',
      hasVideo: false
    },
    {
      id: 'florencia-echeverria-selva',
      title: 'Florencia Echeverría - "Selva Atlántica"',
      location: 'AWASI - 20.06.2024',
      date: '20.06.2024',
      description: 'Florencia Echeverría is an Argentine porcelain sculptor and visual artist who traveled to Awasi Iguazú to learn authentic weaving techniques from the Guarani community. While in residence, the artist collected Misiones red soil to mix with cold porcelain to create a piece inspired by the local flora.',
      image: images.artRainerGross || '',
      hasVideo: true
    }
  ];
  
  const artImmersions = contentfulImmersions.length > 0 ? contentfulImmersions : staticArtImmersions;


  return (
    <div className="art-immersions-page">
      {/* Navigation Header - Atacama variant with destination */}
      <UnifiedNavigation variant="light" destination="atacama" />
      
      {/* Orange vertical line accent */}
      <div className="vertical-accent-line" />
      
      {/* 1. Diptych Hero Section */}
      <ArtDiptych
        leftContent={{
          text: pageContent?.diptychText || 'At Awasi, we believe that Nature and Art are deeply intertwined. They are both valuable, fragile and essential for human beings.',
          backgroundColor: 'var(--color-atacama)'
        }}
        rightContent={{
          video: images.videoUrls.parrotArtImersionsVideo,
          alt: 'Parrot art immersions video'
        }}
      />

      {/* 2. Article Gallery Subheader */}
      <ArticleGallerySubheader
        contentfulText={pageContent?.subheaderText}
        fallbackText="Awasi was conceived from the idea that every journey should connect us with nature and deepen self-discovery. Over the years, we have collaborated with artists, providing a setting amongst nature where each one could find unique inspiration. May art and nature always inspire, elevate and transform us."
      />

      {/* 3. Article Gallery with Filters + Pagination */}
      <ArticleGallery
        immersions={artImmersions}
        itemsPerPage={4}
      />

      {/* 4. Feathers Video */}
      <FeathersVideo
        videoSrc={images.filmVideo1}
        autoPlay={true}
      />

      {/* 5. Feathers Video Footer */}
      <FeathersVideoFooter
        quote={pageContent?.feathersQuote || "At Awasi, we believe that Nature and Art are deeply intertwined."}
        linkText={pageContent?.feathersLinkText || siteSettings?.globalCTAs?.learnMore || "GET IN TOUCH"}
        linkUrl="/our-story"
      />

      {/* 6. Instagram Section - matching Film Collection page */}
      <InstagramSection />

      {/* 7. Footer - Atacama specific */}
      <Footer variant="atacama" />
    </div>
  );
};

export default ArtImmersionsAtacama;